import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { useToast } from '../hooks/useToast';
import DataTable from '../components/common/DataTable';
import ConfirmModal from '../components/common/ConfirmModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import RequestDetailModal from '../components/RequestDetailModal';
import {
  getOpenRequests,
  getAdminTasks,
  approveRequest,
  closeRequest,
  assignWorker,
  getWorkers,
  getWorkerResponseTimes,
  settleRequest,
  confirmRequestPayment,
} from '../utils/api';
import {
  ClipboardList,
  CheckCircle,
  XCircle,
  Clock,
  UserCheck,
  MapPin,
  Filter,
  Calendar,
  AlertTriangle
} from 'lucide-react';
import '../styles/RequestsPage.css';

const RequestsPage = () => {
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('id');
  // O supervisor entra aqui para atribuir trabalhadores; as acções de
  // aprovação, fecho e decisão financeira ficam para o administrador.
  const { user } = useContext(AuthContext);
  const podeAdministrar = ['admin', 'super_admin'].includes(user?.role);
  const podeAtribuir = ['admin', 'super_admin', 'operations_manager'].includes(user?.role);
  const [activeTab, setActiveTab] = useState('open');
  const { success, error } = useToast();
  const [assignModal, setAssignModal] = useState({ open: false, request: null });
  const [selectedWorker, setSelectedWorker] = useState('');
  const [closeModal, setCloseModal] = useState({ open: false, request: null });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detailId, setDetailId] = useState(null); // modal de detalhe (clique na linha)
  const [settleModal, setSettleModal] = useState({ open: false, row: null });
  const [settlePct, setSettlePct] = useState(50);
  const [transferencia, setTransferencia] = useState({ open: false, row: null, referencia: '' });

  // Vindo de uma notificação (?id=N): mostra todas para garantir que aparece destacada
  useEffect(() => {
    if (highlightId) setActiveTab('all');
  }, [highlightId]);

  const { data: openRequests, isLoading: openLoading, execute: fetchOpen } = useApi(getOpenRequests, { immediate: true });
  const { data: adminTasks, isLoading: tasksLoading, execute: fetchTasks } = useApi(getAdminTasks, { immediate: true });
  const { data: workers } = useApi(getWorkers, { immediate: true });
  const { data: responseTimes } = useApi(getWorkerResponseTimes, { immediate: true });

  const handleApprove = async (requestId) => {
    try {
      const res = await approveRequest(requestId);
      success(res?.message || 'Pedido aprovado!');
      fetchOpen();
      fetchTasks();
    } catch (err) {
      // Mostra a mensagem real do backend (antes só dizia "Erro" genérico)
      error(err.response?.data?.message || `Erro ao aprovar (${err.response?.status || 'sem ligação'})`);
    }
  };

  const handleClose = async () => {
    if (!closeModal.request) return;
    try {
      await closeRequest(closeModal.request.request_id);
      success('Pedido fechado!');
      fetchOpen();
      setCloseModal({ open: false, request: null });
    } catch (err) {
      error(err.response?.data?.message || `Erro ao fechar (${err.response?.status || 'sem ligação'})`);
    }
  };

  const handleAssign = async () => {
    if (!assignModal.request || !selectedWorker) return;
    try {
      await assignWorker(assignModal.request.request_id, selectedWorker);
      success('Profissional atribuído com sucesso!');
      fetchOpen();
      setAssignModal({ open: false, request: null });
      setSelectedWorker('');
    } catch (err) {
      error(err.response?.data?.message || `Erro ao atribuir (${err.response?.status || 'sem ligação'})`);
    }
  };

  const getWorkerSuggestions = (serviceType) => {
    if (!workers || !responseTimes) return [];
    return workers
      .filter(w => w.category === serviceType || !serviceType)
      .map(w => {
        const rt = responseTimes.find(r => r.worker_id === w.id);
        return { ...w, avg_response_time: rt?.avg_response_time };
      })
      .sort((a, b) => (a.avg_response_time || Infinity) - (b.avg_response_time || Infinity))
      .slice(0, 5);
  };

  const openSettle = (row) => { setSettleModal({ open: true, row }); setSettlePct(50); };

  const handleSettle = async () => {
    const row = settleModal.row;
    if (!row) return;
    const pct = Number(settlePct);
    if (isNaN(pct) || pct < 0 || pct > 100) { error('Percentagem inválida (0-100)'); return; }
    try {
      const res = await settleRequest(row.request_id, pct);
      success(res?.message || 'Decisão registada');
      setSettleModal({ open: false, row: null });
      fetchOpen();
    } catch (err) {
      error(err.response?.data?.message || 'Erro ao registar decisão');
    }
  };

  const handleConfirmTransfer = async () => {
    const referencia = String(transferencia.referencia || '').trim();
    if (!referencia) { error('Indique a referência da transferência.'); return; }
    try {
      await confirmRequestPayment(transferencia.row.request_id, referencia);
      success('Pagamento confirmado. Os contactos foram libertados.');
      setTransferencia({ open: false, row: null, referencia: '' });
      fetchOpen();
    } catch (err) {
      error(err.response?.data?.message || 'Erro ao confirmar o pagamento');
    }
  };

  const statusBadge = (status) => {
    const styles = {
      open: 'bg-blue-500/20 text-blue-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      awaiting_payment: 'bg-amber-500/20 text-amber-400',
      approved: 'bg-green-500/20 text-green-400',
      in_progress: 'bg-purple-500/20 text-purple-400',
      completed: 'bg-teal-500/20 text-teal-400',
      closed: 'bg-gray-500/20 text-gray-400',
      cancelled: 'bg-red-500/20 text-red-400',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.open}`}>
        {status === 'in_progress' ? 'Em Andamento' :
         status === 'awaiting_payment' ? 'Aguarda pagamento' :
         status === 'open' ? 'Aberta' :
         status === 'pending' ? 'Pendente' :
         status === 'approved' ? 'Aprovada' :
         status === 'completed' ? 'Completada' :
         status === 'closed' ? 'Fechada' :
         status === 'cancelled' ? 'Cancelada' : status}
      </span>
    );
  };

  const priorityBadge = (priority) => {
    const styles = {
      high: 'bg-red-500/20 text-red-400',
      medium: 'bg-yellow-500/20 text-yellow-400',
      low: 'bg-green-500/20 text-green-400',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[priority] || styles.low}`}>
        {priority === 'high' ? 'Alta' : priority === 'medium' ? 'Média' : 'Baixa'}
      </span>
    );
  };

  const tabs = [
    { id: 'open', label: 'Abertas', count: (openRequests || []).filter(r => r.status === 'open').length },
    { id: 'pending', label: 'Pendentes', count: (openRequests || []).filter(r => r.status === 'pending').length },
    { id: 'awaiting_payment', label: 'Aguarda pagamento', count: (openRequests || []).filter(r => r.status === 'awaiting_payment').length },
    { id: 'in_progress', label: 'Em Andamento', count: (openRequests || []).filter(r => r.status === 'in_progress').length },
    { id: 'disputed', label: 'Em mediação', count: (openRequests || []).filter(r => r.payment_status === 'disputed').length },
    { id: 'completed', label: 'Completadas', count: (openRequests || []).filter(r => r.status === 'completed').length },
    { id: 'all', label: 'Todas', count: (openRequests || []).length },
  ];

  const requestColumns = [
    { key: 'request_id', label: 'ID', width: '60px' },
    { key: 'client_name', label: 'Cliente' },
    { key: 'worker_name', label: 'Profissional', render: (row) => (
      row.worker_name
        ? <span className="text-gray-200">{row.worker_name}</span>
        : <span className="text-gray-500 text-xs">— sem atribuição</span>
    )},
    { key: 'service_type', label: 'Serviço' },
    { key: 'description', label: 'Descrição', render: (row) => (
      <span className="truncate max-w-[200px] block" title={row.description}>{row.description}</span>
    )},
    { key: 'status', label: 'Status', render: (row) => (
      <div className="flex flex-col gap-1 items-start">
        {statusBadge(row.status)}
        {row.payment_status === 'disputed' && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-500/20 text-red-400">Em mediação</span>
        )}
        {row.status === 'in_progress' && row.payment_status !== 'disputed' && Number(row.released_percent) > 0 && (
          <span className="text-[10px] text-purple-300">{Number(row.released_percent)}% libertado</span>
        )}
      </div>
    )},
    { key: 'priority', label: 'Prioridade', render: (row) => priorityBadge(row.priority || 'low') },
    { key: 'location', label: 'Local', render: (row) => (
      <div className="flex items-center gap-1 text-gray-400">
        <MapPin className="w-3 h-3" />
        <span className="text-xs truncate max-w-[100px]">{row.location || 'N/A'}</span>
      </div>
    )},
    { key: 'created_at', label: 'Criada', render: (row) => 
      row.created_at ? new Date(row.created_at).toLocaleDateString('pt-PT') : '-' 
    },
    {
      key: 'actions',
      label: 'Ações',
      sortable: false,
      render: (row) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {row.status === 'open' && podeAdministrar && (
            <button
              onClick={() => handleApprove(row.request_id)}
              className="p-1.5 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors"
              title="Aprovar"
            >
              <CheckCircle className="w-4 h-4" />
            </button>
          )}
          {['open', 'pending', 'approved'].includes(row.status) && podeAtribuir && (
            <button
              onClick={() => setAssignModal({ open: true, request: row })}
              className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors"
              title="Atribuir profissional"
            >
              <UserCheck className="w-4 h-4" />
            </button>
          )}
          {row.status === 'awaiting_payment' && podeAdministrar && (
            <button
              onClick={() => setTransferencia({ open: true, row, referencia: '' })}
              className="px-2 py-1.5 rounded-lg bg-teal-500/10 text-teal-300 hover:bg-teal-500/20 transition-colors text-xs font-semibold"
              title="Confirmar que a transferência entrou"
            >
              Transferência
            </button>
          )}
          {(row.payment_status === 'held' || row.payment_status === 'disputed') && podeAdministrar && (
            <button
              onClick={() => openSettle(row)}
              className="px-2 py-1.5 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors text-xs font-semibold"
              title="Em mediação — decidir percentagem justa para o profissional"
            >
              Decidir
            </button>
          )}
          {podeAdministrar && (
            <button
              onClick={() => setCloseModal({ open: true, request: row })}
              className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
              title="Fechar"
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const getTabData = () => {
    let data = activeTab === 'all'
      ? (openRequests || [])
      : activeTab === 'disputed'
        ? (openRequests || []).filter(r => r.payment_status === 'disputed')
        : (openRequests || []).filter(r => r.status === activeTab);

    // Filtro por intervalo de datas (criada)
    if (dateFrom) data = data.filter(r => r.created_at && new Date(r.created_at) >= new Date(dateFrom));
    if (dateTo)   data = data.filter(r => r.created_at && new Date(r.created_at) <= new Date(`${dateTo}T23:59:59`));
    return data;
  };

  const isLoading = openLoading || tasksLoading;

  return (
    <div className="requests-admin-page space-y-6">
      {/* Header */}
      <div className="requests-admin-hero"><div><span>Fluxo de serviços</span><h1>Gestão de pedidos</h1><p>Acompanhe cada pedido desde a entrada até à conclusão.</p></div><div className="requests-admin-focus"><AlertTriangle/><span><small>Requerem atenção</small><strong>{(openRequests || []).filter(r => r.priority === 'high' || r.payment_status === 'disputed').length}</strong></span></div></div>

      {/* Stats */}
      <div className="requests-admin-tabs grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`request-admin-tab p-4 rounded-xl border transition-all text-left
              ${activeTab === tab.id 
                ? 'bg-teal-500/10 border-teal-500/30' 
                : 'bg-gray-800 border-gray-700 hover:border-gray-600'}`}
          >
            <p className={`text-2xl font-bold ${activeTab === tab.id ? 'text-teal-400' : 'text-white'}`}>
              {tab.count}
            </p>
            <p className="text-gray-400 text-sm">{tab.label}</p>
          </button>
        ))}
      </div>

      {/* Filtro por datas */}
      <div className="requests-admin-filters flex items-center gap-3 flex-wrap bg-gray-800 border border-gray-700 rounded-xl px-4 py-3">
        <span className="flex items-center gap-2 text-gray-400 text-sm">
          <Filter className="w-4 h-4" /> Filtrar por data:
        </span>
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <Calendar className="w-4 h-4 text-teal-400" /> De
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <Calendar className="w-4 h-4 text-teal-400" /> Até
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </label>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="text-teal-400 text-sm hover:text-teal-300">
            Limpar
          </button>
        )}
        {highlightId && (
          <span className="ml-auto text-xs text-teal-400 bg-teal-500/10 border border-teal-500/30 rounded-full px-3 py-1">
            Destaque: pedido #{highlightId}
          </span>
        )}
      </div>

      {/* Table */}
      {isLoading && !openRequests ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <DataTable
          data={getTabData()}
          columns={requestColumns}
          pageSize={10}
          emptyMessage="Nenhum pedido encontrado."
          onRowClick={(row) => setDetailId(row.request_id)}
          rowClassName={(row) => String(row.request_id) === String(highlightId)
            ? 'bg-teal-500/10 ring-1 ring-inset ring-teal-500/40'
            : ''}
        />
      )}

      {/* Tarefas do admin (tickets atribuídos) */}
      <div className="requests-admin-tasks bg-gray-800 border border-gray-700 rounded-xl p-5">
        <h2 className="text-white font-semibold flex items-center gap-2 mb-3">
          <ClipboardList className="w-5 h-5 text-teal-400" /> As Minhas Tarefas
        </h2>
        {tasksLoading ? (
          <LoadingSpinner size="sm" />
        ) : (adminTasks || []).length === 0 ? (
          <p className="text-gray-500 text-sm">Sem tarefas atribuídas de momento.</p>
        ) : (
          <ul className="space-y-2">
            {(adminTasks || []).slice(0, 8).map((task, i) => (
              <li key={task.ticket_id || task.id || i}
                className="flex items-center justify-between gap-3 bg-gray-700/40 border border-gray-700 rounded-lg px-3 py-2">
                <span className="text-gray-200 text-sm truncate">
                  {task.subject || task.title || task.service_type || `Tarefa #${task.ticket_id || task.id || i + 1}`}
                </span>
                <span className="flex items-center gap-1 text-gray-400 text-xs whitespace-nowrap">
                  <Clock className="w-3 h-3" />
                  {task.created_at ? new Date(task.created_at).toLocaleString('pt-PT') : '-'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Assign Worker Modal */}
      {assignModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-2">Atribuir profissional</h3>
            <p className="text-gray-400 text-sm mb-4">
              Pedido #{assignModal.request?.request_id} - {assignModal.request?.service_type}
            </p>

            {/* Suggestions */}
            <div className="mb-4">
              <p className="text-sm text-gray-300 font-medium mb-2">Sugestões (melhor tempo de resposta):</p>
              <div className="space-y-2">
                {getWorkerSuggestions(assignModal.request?.service_type).map((worker, idx) => (
                  <div 
                    key={worker.id}
                    onClick={() => setSelectedWorker(worker.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all
                      ${selectedWorker === worker.id 
                        ? 'border-teal-500 bg-teal-500/10' 
                        : 'border-gray-700 hover:border-gray-600 bg-gray-700/50'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-medium">{worker.full_name}</p>
                        <p className="text-gray-400 text-xs">{worker.category}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-teal-400 text-sm font-medium">
                          {worker.avg_response_time ? `${(worker.avg_response_time / 60).toFixed(1)} min` : 'N/A'}
                        </p>
                        <p className="text-gray-500 text-xs">tempo médio</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Manual Selection */}
            <div className="mb-4">
              <label className="block text-gray-400 text-sm mb-1">Ou selecione manualmente:</label>
              <select
                value={selectedWorker}
                onChange={(e) => setSelectedWorker(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white 
                           focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Selecionar profissional...</option>
                {(workers || []).map(w => (
                  <option key={w.id} value={w.id}>{w.full_name} - {w.category}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setAssignModal({ open: false, request: null }); setSelectedWorker(''); }}
                className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedWorker}
                className="px-4 py-2 rounded-lg bg-teal-500 text-white hover:bg-teal-600 transition-colors 
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Atribuir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de decisão de disputa (substitui window.prompt) */}
      {settleModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-1">Decidir mediação</h3>
            <p className="text-gray-400 text-sm mb-4">
              Trabalho #{settleModal.row?.request_id} · Valor: Kz {Number(settleModal.row?.agreed_price || 0).toLocaleString('pt-PT')}
              {Number(settleModal.row?.released_percent) > 0 && <> · já libertado: {Number(settleModal.row?.released_percent)}%</>}
            </p>
            <label className="block text-gray-400 text-sm mb-2">Percentagem FINAL para o profissional:</label>
            <div className="flex gap-2 mb-2">
              {[0, 25, 50, 75, 100].map(p => (
                <button key={p} onClick={() => setSettlePct(p)}
                  className={`px-3 py-1.5 rounded-lg border text-sm font-semibold transition-all
                    ${Number(settlePct) === p ? 'border-teal-500 bg-teal-500/10 text-teal-400' : 'border-gray-700 text-gray-400 hover:border-gray-600'}`}>
                  {p}%
                </button>
              ))}
            </div>
            <input type="number" min="0" max="100" value={settlePct}
              onChange={(e) => setSettlePct(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500" />
            <p className="text-xs text-gray-500 mt-2">
              Profissional recebe {Number(settlePct) || 0}% (Kz {(Number(settleModal.row?.agreed_price || 0) * (Number(settlePct) || 0) / 100).toLocaleString('pt-PT')}); o resto é reembolsado ao cliente.
            </p>
            <div className="flex gap-3 justify-end mt-5">
              <button onClick={() => setSettleModal({ open: false, row: null })}
                className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors">Cancelar</button>
              <button onClick={handleSettle}
                className="px-4 py-2 rounded-lg bg-teal-500 text-white hover:bg-teal-600 transition-colors">Registar Decisão</button>
            </div>
          </div>
        </div>
      )}

      {/* Detalhe completo do trabalho (clique na linha) */}
      {detailId && (
        <RequestDetailModal
          requestId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={fetchOpen}
        />
      )}

      {transferencia.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-1">Confirmar transferência</h3>
            <p className="text-gray-400 text-sm mb-4">
              Pedido #{transferencia.row?.request_id}. Indique a referência que viu no extracto.
              Isto liberta os contactos — só confirme se o dinheiro entrou.
            </p>
            <input
              value={transferencia.referencia}
              onChange={(e) => setTransferencia((t) => ({ ...t, referencia: e.target.value }))}
              placeholder="Referência da transferência"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <div className="flex gap-3 justify-end mt-5">
              <button onClick={() => setTransferencia({ open: false, row: null, referencia: '' })}
                className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors">Cancelar</button>
              <button onClick={handleConfirmTransfer}
                className="px-4 py-2 rounded-lg bg-teal-500 text-white hover:bg-teal-600 transition-colors">Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {/* Close Confirmation */}
      <ConfirmModal
        isOpen={closeModal.open}
        title="Fechar pedido"
        message={`Tem certeza que deseja fechar o pedido #${closeModal.request?.request_id}?`}
        onConfirm={handleClose}
        onCancel={() => setCloseModal({ open: false, request: null })}
        confirmText="Fechar"
        type="warning"
      />
    </div>
  );
};

export default RequestsPage;
